import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, TrendingDown, MousePointerClick, Keyboard, Radio } from "lucide-react";

/**
 * Magic-flow funnel admin card.
 *
 * Visualises the 5-step landing-hero conversion funnel:
 *   Typed → Picked → Clicked Analyze → Signup completed → Analysis ready.
 *
 * Also splits submissions by source (autocomplete pick vs manual typing)
 * so we can see whether forcing canonical addresses via Google Places
 * actually moves the signup-completion needle.
 */
export default function MagicFunnelCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/analytics/magic-funnel?days=30");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  // Hide entirely when there's zero data — keeps the dashboard clean pre-launch.
  const hasAny = data.steps.some((s) => s.total > 0);
  if (!hasAny) return null;

  const peak = Math.max(...data.steps.map((s) => s.total), 1);
  const src = data.submitted_by_source || {};
  const totalSubmits = (src.autocomplete_pick || 0) + (src.manual_typed || 0) + (src.empty || 0);
  const autopct = totalSubmits ? Math.round((src.autocomplete_pick / totalSubmits) * 100) : 0;

  return (
    <div className="card" data-testid="admin-magic-funnel-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs text-[var(--ink-3)] uppercase tracking-wider font-bold flex items-center gap-1.5">
            <Sparkles size={11} className="text-emerald-600" /> Magic-flow funnel
          </div>
          <h3 className="font-display font-bold text-lg leading-tight">
            Hero address → live analysis
          </h3>
          <p className="text-xs text-[var(--ink-2)] mt-0.5">
            Last 30 days · how landing-page address typers become activated users.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)]">
            Submit → Analysis
          </div>
          <div className="font-display font-extrabold text-2xl text-emerald-700" data-testid="submit-to-ready-pct">
            {data.submit_to_ready_pct}%
          </div>
        </div>
      </div>

      {/* 5-step horizontal funnel */}
      <ul className="space-y-1.5 mb-4" data-testid="magic-funnel-steps">
        {data.steps.map((s, i) => {
          const widthPct = Math.max(4, Math.round((s.total / peak) * 100));
          const isDropoff = s.drop_off_pct != null && s.drop_off_pct > 50;
          return (
            <li key={s.event} className="flex items-center gap-3 text-sm" data-testid={`magic-funnel-step-${i}`}>
              <span className="w-44 shrink-0 text-[var(--ink-2)] truncate">
                {s.label}
              </span>
              <div className="flex-1 relative h-6 bg-[var(--bg-2)] rounded">
                <div
                  className="absolute inset-y-0 left-0 rounded"
                  style={{
                    width: `${widthPct}%`,
                    background: "linear-gradient(90deg,#10B981,#047857)",
                  }}
                />
                <span className="absolute inset-y-0 left-2 flex items-center text-white font-bold text-xs mix-blend-luminosity">
                  {s.total.toLocaleString()}
                </span>
              </div>
              <span className="w-20 text-right shrink-0">
                {s.conversion_from_previous_pct != null && (
                  <span className={`text-xs font-bold ${isDropoff ? "text-rose-700" : "text-emerald-700"}`}>
                    {isDropoff && <TrendingDown size={10} className="inline mb-0.5" />} {s.conversion_from_previous_pct}%
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Source split */}
      {totalSubmits > 0 && (
        <div className="border-t border-[var(--line)] pt-3">
          <div className="text-[11px] text-[var(--ink-3)] uppercase tracking-wider font-bold mb-2">
            How users submitted
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <SrcKpi
              icon={MousePointerClick}
              label="Picked from dropdown"
              value={src.autocomplete_pick || 0}
              pct={autopct}
              tone="emerald"
              testid="src-autocomplete"
            />
            <SrcKpi
              icon={Keyboard}
              label="Typed manually"
              value={src.manual_typed || 0}
              pct={totalSubmits ? Math.round((src.manual_typed / totalSubmits) * 100) : 0}
              tone="amber"
              testid="src-manual"
            />
            <SrcKpi
              label="Empty submit"
              value={src.empty || 0}
              pct={totalSubmits ? Math.round((src.empty / totalSubmits) * 100) : 0}
              tone="ink"
              testid="src-empty"
            />
          </div>
        </div>
      )}

      <UtmBreakdown rows={data.by_utm_source || []} />
    </div>
  );
}

function UtmBreakdown({ rows }) {
  if (!rows || rows.length === 0) return null;
  const peakAnalyzed = Math.max(...rows.map((r) => r.analyzed), 1);
  return (
    <div className="border-t border-[var(--line)] pt-3 mt-4" data-testid="magic-funnel-utm-breakdown">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] text-[var(--ink-3)] uppercase tracking-wider font-bold flex items-center gap-1.5">
          <Radio size={11} /> Traffic source attribution
        </div>
        <div className="text-[10px] text-[var(--ink-3)]">
          typed → analyzed conversion by <code className="font-mono">utm_source</code>
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-[var(--line)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)] text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-bold">
            <tr>
              <th className="text-left px-3 py-1.5">Source</th>
              <th className="text-right px-2 py-1.5">Typed</th>
              <th className="text-right px-2 py-1.5">Signed up</th>
              <th className="text-right px-2 py-1.5">Analyzed</th>
              <th className="text-right px-3 py-1.5">T→A</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const ta = r.typed_to_analyzed_pct || 0;
              const tone =
                ta >= 50 ? "text-emerald-700" : ta >= 25 ? "text-amber-700" : "text-rose-700";
              const barPct = Math.max(2, Math.round((r.analyzed / peakAnalyzed) * 100));
              return (
                <tr
                  key={r.utm_source}
                  className="border-t border-[var(--line)]"
                  data-testid={`utm-row-${r.utm_source}`}
                >
                  <td className="px-3 py-1.5 font-semibold text-[var(--ink)]">
                    {r.utm_source}
                  </td>
                  <td className="px-2 py-1.5 text-right text-[var(--ink-2)]">
                    {r.typed.toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 text-right text-[var(--ink-2)]">
                    {r.registered.toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 text-right text-[var(--ink)]">
                    <span className="inline-flex items-center gap-2">
                      <span className="font-bold">{r.analyzed.toLocaleString()}</span>
                      <span
                        className="inline-block h-1.5 rounded"
                        style={{
                          width: `${barPct}px`,
                          maxWidth: "60px",
                          background: "linear-gradient(90deg,#10B981,#047857)",
                        }}
                      />
                    </span>
                  </td>
                  <td className={`px-3 py-1.5 text-right font-bold ${tone}`}>
                    {ta}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-[var(--ink-3)] mt-1.5">
        Sources are auto-captured from <code className="font-mono">?utm_source=…</code> URL params (30-day attribution window). Visitors without UTMs roll up as <strong>direct</strong>.
      </div>
    </div>
  );
}

function SrcKpi({ icon: Icon, label, value, pct, tone, testid }) {
  const toneClasses = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    ink: "text-[var(--ink-2)] bg-[var(--bg-2)] border-[var(--line)]",
  };
  return (
    <div className={`rounded-lg border px-2.5 py-2 ${toneClasses[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 opacity-80">
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className="font-display font-extrabold text-lg leading-tight">{value} <span className="text-xs font-bold opacity-75">· {pct}%</span></div>
    </div>
  );
}
